import { ConfigReader } from '../config/ConfigReader';

export interface StockEventRowResponse {
  movementId: string;
  type: 'INBOUND' | 'OUTBOUND';
  qty: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  trackingNumber?: string;
  carrier?: string;
  createdAt: string;
  locationId?: string;
  auditor: string;
  orderId?: string;
}

export interface StockStatisticResponse {
  totalQuantity: number;
  totalReservedQuantity: number;
  byLocation: Array<{
    locationId: string;
    quantity: number;
    reservedOutbound: number;
  }>;
}

export interface MonthlyStockSummary {
  month: string;
  inboundQty: number;
  outboundQty: number;
  endingQty: number;
}

export interface StockStatusResponse {
  statistic: StockStatisticResponse;
  monthly: MonthlyStockSummary[];
}

export interface GetStockHistoryResponse {
  stockStatus: StockStatusResponse;
  events: StockEventRowResponse[];
  nextToken?: string;
}

export interface StockResponse {
  movementId: string;
  success: boolean;
  message: string;
}

export interface ProductLookupResponse {
  productId: number;
  sku: string;
  name: string;
  nameKor?: string;
  brand?: string;
  productLink?: string;
  productImageUrls?: string[];
  category?: string[];
  size?: string;
  pack?: string;
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  status?: string;
  description?: string;
  barcode?: string;
}

export interface DeleoInboundRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  effectiveAt?: string;
  locationId?: string;
  orderId?: string;
  auditor: string;
}

export interface InboundStockRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  effectiveAt?: string;
  locationId?: string;
  orderId?: string;
  auditor: string;
}

export enum OutboundStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  INBOUND_CANCEL = 'INBOUND_CANCEL',
  OUTBOUND_CANCEL = 'OUTBOUND_CANCEL',
}

export interface OutboundStockRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  effectiveAt?: string;
  locationId?: string;
  status?: OutboundStatus;
  orderId?: string;
  auditor: string;
}

export interface StockLocationInfo {
  locationId: string;
  quantity: number;
  reservedOutbound: number;
}

export interface DetailedStockStatus {
  totalQuantity: number;
  totalReservedQuantity: number;
  byLocation: StockLocationInfo[];
  asOf: string;
}

export interface StockStatusResponse {
  productStocks: Record<string, DetailedStockStatus>;
}

export class StockClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor() {
    const config = ConfigReader.getInstance().getConfig();
    this.baseUrl = config.apiUrl;
    this.apiKey = config.apiKey;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'X-API-KEY': this.apiKey,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  public async getStockHistory(
    params: { productId?: number; sku?: string },
    limit?: number,
    nextToken?: string,
  ): Promise<GetStockHistoryResponse> {
    const searchParams = new URLSearchParams();

    if (params.productId) {
      searchParams.append('productId', params.productId.toString());
    }
    if (params.sku) {
      searchParams.append('sku', params.sku);
    }
    if (limit) {
      searchParams.append('limit', limit.toString());
    }
    if (nextToken) {
      searchParams.append('nextToken', nextToken);
    }

    const endpoint = `/stock/history?${searchParams.toString()}`;

    return this.makeRequest<GetStockHistoryResponse>(endpoint);
  }

  public async getProductBySku(sku: string): Promise<ProductLookupResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', sku);

    const endpoint = `/products?${searchParams.toString()}`;

    return (await this.makeRequest<ProductLookupResponse[]>(endpoint))[0]!;
  }

  public async getProductsBySkus(skus: string[]): Promise<ProductLookupResponse[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', skus.join(','));

    const endpoint = `/products?${searchParams.toString()}`;

    return this.makeRequest<ProductLookupResponse[]>(endpoint);
  }

  public async deleoInbound(request: DeleoInboundRequest): Promise<StockResponse> {
    return this.makeRequest<StockResponse>('/stock/deleoInbound', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  public async inboundStock(request: InboundStockRequest): Promise<StockResponse> {
    return this.makeRequest<StockResponse>('/stock/inbound', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  public async outboundStock(request: OutboundStockRequest): Promise<StockResponse> {
    return this.makeRequest<StockResponse>('/stock/outbound', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  public async getStockStatus(skus: string[]): Promise<StockStatusResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', skus.join(','));

    const endpoint = `/stock/status?${searchParams.toString()}`;
    return this.makeRequest<StockStatusResponse>(endpoint);
  }

  public async getReadyToBeOutbonded(): Promise<import('./OMSClient').OrderListResponse> {
    return this.makeRequest<import('./OMSClient').OrderListResponse>('/stock/readyToBeOutbonded');
  }

  public async exportStockStatus(): Promise<Blob> {
    const url = `${this.baseUrl}/stock/exportStockStatus`;
    const response = await fetch(url, {
      headers: {
        'X-API-KEY': this.apiKey,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  }
}
